/**
 * Geography loaders for the dashboard map.
 *
 * Province polygons load eagerly (~600 KB). City polygons (~1.4 MB,
 * 1,618 features) lazy-load when the user zooms past the city cutoff.
 */

import type { Feature, FeatureCollection, Geometry } from 'geojson';

export interface ProvinceProps {
  /** Province name, matches view_city_metrics.province. */
  ADM2_EN:    string;
  /** 10-digit PSGC code. */
  ADM2_PCODE: string;
  [k: string]: unknown;
}

/** faeldon 2023 dataset city properties */
export interface CityProps {
  adm3_psgc:  string;   // normalized to string in fetch-geo
  adm3_en:    string;
  adm2_psgc:  string | number;
  geo_level:  string;
  [k: string]: unknown;
}

export async function loadProvinces(): Promise<FeatureCollection<Geometry, ProvinceProps>> {
  const mod = await import('./ph-provinces.geo.json');
  return mod.default as FeatureCollection<Geometry, ProvinceProps>;
}

export async function loadCities(): Promise<FeatureCollection<Geometry, CityProps>> {
  const mod = await import('./ph-cities.geo.json');
  return mod.default as FeatureCollection<Geometry, CityProps>;
}

/** Sequential SoP color ramp using the kit's sequentialPalette. */
export function sopColor(value: number, max: number): string {
  if (!Number.isFinite(value) || value <= 0 || max <= 0) return '#E2E8EE';
  const t = Math.min(1, Math.sqrt(value / max));
  const stops = ['#DCEAF5', '#7AA7CB', '#0E5A96', '#0D2E4A'];
  const idx   = t * (stops.length - 1);
  const i     = Math.floor(idx);
  const f     = idx - i;
  if (i >= stops.length - 1) return stops[stops.length - 1];
  return mixHex(stops[i], stops[i + 1], f);
}

function mixHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const r  = Math.round((pa >> 16 & 255) * (1 - t) + (pb >> 16 & 255) * t);
  const g  = Math.round((pa >>  8 & 255) * (1 - t) + (pb >>  8 & 255) * t);
  const bl = Math.round((pa       & 255) * (1 - t) + (pb       & 255) * t);
  return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0')}`;
}

export type ProvinceFeature = Feature<Geometry, ProvinceProps>;
export type CityFeature     = Feature<Geometry, CityProps>;
